import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo, ReactNode } from 'react';
import { useNotification } from '../components/Notification/NotificationContext';
import { useKeycloakAuth } from './KeycloakAuthProvider';
import { useKnowledgeGraphApi } from './ApiContext';
import { KG_PROGRESS_MESSAGES, KG_ERROR_MESSAGES } from '../constants/kgMessages';

const KG_ACTIVE_IMPORT_KEY = 'finbot_kg_active_import_job';

// Progress step interface matching backend ProgressStep schema
export interface ProgressStep {
  id: string;
  name: string;
  progress: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  error?: string | null;
}

// Enhanced progress data interface with standardized backend fields
export interface KGImportProgressData {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'skipped';
  current_step: ProgressStep; // Currently executing step
  all_steps?: ProgressStep[];
  processed_documents: number;
  total_documents: number;
  failed_documents: number;
  nodes_created?: number;
  relationships_created?: number;
  embeddings_created?: number;
  elapsed_time: number;
  estimated_completion?: string;
  message?: string;
  error_details?: string;
}

// Progress tracker options
export interface KGImportProgressOptions {
  pollInterval?: number; // milliseconds, default 2000
  maxRetries?: number; // max retry attempts for failed requests, default 3
  notificationEnabled?: boolean; // show notifications, default true
  stickyNotifications?: boolean; // sticky notifications for long ops, default false
  onProgress?: (data: KGImportProgressData) => void;
  onComplete?: (data: KGImportProgressData) => void;
  onError?: (error: string) => void;
  onCancel?: () => void;
}

interface KGImportProgressContextValue {
  // Current progress state
  progressData: KGImportProgressData | null;
  isPolling: boolean;
  error: string | null;
  retryCount: number;
  activeJobId: string | null;
  isDismissed: boolean;

  // Control functions
  startTracking: (jobId: string, options?: KGImportProgressOptions) => void;
  stopTracking: () => void;
  cancelImport: () => Promise<boolean>;
  dismissNotification: () => void;
  retryImport: () => void;
  setSuppressGlobalIndicator: (suppress: boolean) => void;
  isSuppressed: boolean;

  // Computed values for convenience
  isCompleted: boolean;
  isFailed: boolean;
  isCancelled: boolean;
  isSkipped: boolean;
  isRunning: boolean;
  progressPercentage: number;
  currentStep: string; // Current step ID
  allSteps: ProgressStep[]; // All steps with individual progress
  documentsInfo: {
    processed: number;
    total: number;
    failed: number;
  } | null;
  performanceInfo: {
    nodesCreated: number;
    relationshipsCreated: number;
    embeddingsCreated: number;
    elapsedTime: number;
  } | null;
}

const KGImportProgressContext = createContext<KGImportProgressContextValue | undefined>(undefined);

interface KGImportProgressProviderProps {
  children: ReactNode;
}

/**
 * Global KG Import Progress Provider that maintains progress tracking state
 * across component unmounts and tab changes. This ensures that import progress
 * tracking continues even when the user navigates away from the import page.
 */
export const KGImportProgressProvider: React.FC<KGImportProgressProviderProps> = ({ children }) => {
  const kgApi = useKnowledgeGraphApi();
  const [progressData, setProgressData] = useState<KGImportProgressData | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [notificationId, setNotificationId] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isSuppressed, setIsSuppressed] = useState(false);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const notificationIdRef = useRef<string | null>(null);
  const previousDataRef = useRef<KGImportProgressData | null>(null);
  const optionsRef = useRef<KGImportProgressOptions>({});
  const consecutiveErrorsRef = useRef(0);
  const isPollingActiveRef = useRef(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Keep notificationIdRef in sync with notificationId state
  useEffect(() => {
    notificationIdRef.current = notificationId;
  }, [notificationId]);

  const { addNotification, removeNotification } = useNotification();
  const { getAccessToken: _getAccessToken, refreshAccessToken } = useKeycloakAuth();

  // Get authentication token with proper error handling using Keycloak
  // Ensures token is refreshed before use to prevent 401 errors during long-running polls
  const getAuthToken = useCallback(async (): Promise<string> => {
    // Refresh token before each polling request to ensure it's valid
    const refreshedToken = await refreshAccessToken();

    if (!refreshedToken) {
      throw new Error('Không có token xác thực');
    }

    return refreshedToken;
  }, [refreshAccessToken]);

  // Cleanup function - stable reference using ref
  const cleanup = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('KG Import Context: Cleaning up polling resources');
    }

    // Clear polling interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      if (process.env.NODE_ENV === 'development') {
        console.log('KG Import Context: Polling interval cleared');
      }
    }

    // Clear retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
      if (process.env.NODE_ENV === 'development') {
        console.log('KG Import Context: Retry timeout cleared');
      }
    }

    // Reset flags
    isPollingActiveRef.current = false;
    consecutiveErrorsRef.current = 0;
    setIsPolling(false);
    setRetryCount(0);

    // Remove sticky notifications using ref to avoid dependency
    if (notificationIdRef.current) {
      removeNotification(notificationIdRef.current);
      setNotificationId(null);
      notificationIdRef.current = null;
      if (process.env.NODE_ENV === 'development') {
        console.log('KG Import Context: Notification removed');
      }
    }
  }, [removeNotification]);

  // Poll progress with enhanced error handling and circuit breaker
  const pollProgress = useCallback(
    async (jobId: string) => {
      if (!jobId) {
        console.warn('KG Import Context: Skipping poll - no jobId provided');
        return;
      }

      // Prevent multiple concurrent polls
      if (isPollingActiveRef.current) {
        if (process.env.NODE_ENV === 'development') {
          console.log('KG Import Context: Poll already active, skipping');
        }
        return;
      }

      // Circuit breaker - stop after too many consecutive errors
      // Increased threshold for long-running KG imports
      if (consecutiveErrorsRef.current >= 15) {
        if (process.env.NODE_ENV === 'development') {
          console.log('KG Import Context: Circuit breaker triggered, stopping polling');
        }
        setIsPolling(false);
        setError(KG_ERROR_MESSAGES.maxRetriesReached);
        cleanup();
        return;
      }

      isPollingActiveRef.current = true;

      // Add debug logging for polling attempts
      if (process.env.NODE_ENV === 'development') {
        console.log('KG Import Context: Polling status for job', jobId);
      }

      try {
        // Create timeout controller for long-running KG imports
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout for status requests

        // Refresh token before making request to ensure it's valid
        await getAuthToken();

        const data: KGImportProgressData = await kgApi.getImportJobStatus(jobId, controller.signal);

        clearTimeout(timeoutId); // Clear timeout on successful response

        // Log progress data for debugging (only in development)
        if (process.env.NODE_ENV === 'development') {
          console.log('KG Import Context Progress Update:', {
            jobId,
            status: data.status,
            step: data.current_step,
            nodes: data.nodes_created,
            relationships: data.relationships_created,
            documents: `${data.processed_documents}/${data.total_documents}`,
            message: data.message,
            shouldContinuePolling: !['completed', 'failed', 'cancelled', 'skipped'].includes(data.status)
          });
        }

        // Check for significant progress updates to provide immediate feedback
        const prevData = previousDataRef.current;
        let hasSignificantUpdate = false;

        if (prevData && data) {
          const nodesDiff = (data.nodes_created ?? 0) - (prevData.nodes_created ?? 0);
          const relsDiff = (data.relationships_created ?? 0) - (prevData.relationships_created ?? 0);

          // Significant update if nodes increased by 1000+ or relationships by 500+
          hasSignificantUpdate = nodesDiff >= 1000 || relsDiff >= 500;
        }

        setProgressData(data);
        setError(null);
        setRetryCount(0); // Reset retry count on success
        consecutiveErrorsRef.current = 0; // Reset circuit breaker on success
        previousDataRef.current = data; // Store for next comparison

        // Call progress callback using ref
        optionsRef.current.onProgress?.(data);

        // Show brief notification for significant progress updates
        if (hasSignificantUpdate && optionsRef.current.notificationEnabled && !optionsRef.current.stickyNotifications) {
          addNotification({
            type: 'success',
            title: KG_PROGRESS_MESSAGES.significantProgress,
            message: `${(data.nodes_created ?? 0).toLocaleString()} ${KG_PROGRESS_MESSAGES.nodesCreated} • ${(data.relationships_created ?? 0).toLocaleString()} ${KG_PROGRESS_MESSAGES.relationshipsCreated}`,
            duration: 2000
          });
        }

        // Update sticky notification using ref with enhanced progress info
        if (optionsRef.current.notificationEnabled && notificationIdRef.current && optionsRef.current.stickyNotifications) {
          removeNotification(notificationIdRef.current);
          let progressMessage = data.message || `${data.processed_documents}/${data.total_documents} tài liệu`;

          // Enhanced message with nodes created
          if ((data.nodes_created ?? 0) > 0) {
            progressMessage += ` • ${data.nodes_created} ${KG_PROGRESS_MESSAGES.nodesCreated}`;
          }
          if ((data.relationships_created ?? 0) > 0) {
            progressMessage += ` • ${data.relationships_created} ${KG_PROGRESS_MESSAGES.relationshipsCreated}`;
          }

          const newNotificationId = addNotification({
            type: data.status === 'failed' ? 'error' : data.status === 'skipped' ? 'warning' : 'info',
            title:
              data.status === 'running'
                ? KG_PROGRESS_MESSAGES.importRunning
                : data.status === 'completed'
                  ? KG_PROGRESS_MESSAGES.importCompleted
                  : data.status === 'failed'
                    ? KG_PROGRESS_MESSAGES.importFailed
                    : data.status === 'skipped'
                      ? KG_PROGRESS_MESSAGES.importSkipped
                      : KG_PROGRESS_MESSAGES.importRunning,
            message: progressMessage,
            duration: data.status === 'running' ? 0 : 5000,
            autoClose: data.status !== 'running'
          });
          setNotificationId(newNotificationId);
          notificationIdRef.current = newNotificationId;
        }

        // Handle completion states - only cleanup for terminal states
        if (data.status === 'completed') {
          if (process.env.NODE_ENV === 'development') {
            console.log('KG Import Context: Job completed, stopping polling');
          }
          setIsPolling(false);

          // Show success notification
          addNotification({
            type: 'success',
            title: KG_PROGRESS_MESSAGES.importCompleted,
            message: `${data.processed_documents}/${data.total_documents} tài liệu • ${(data.nodes_created ?? 0).toLocaleString()} nodes • ${(data.relationships_created ?? 0).toLocaleString()} quan hệ`,
            duration: 5000
          });

          // Call completion callback and cleanup
          localStorage.removeItem(KG_ACTIVE_IMPORT_KEY);
          optionsRef.current.onComplete?.(data);
          cleanup();
        } else if (data.status === 'skipped') {
          if (process.env.NODE_ENV === 'development') {
            console.log('KG Import Context: Job skipped (duplicate detected), stopping polling');
          }
          setIsPolling(false);

          // Success notification is handled by onComplete callback in component
          // to avoid duplicate notifications

          localStorage.removeItem(KG_ACTIVE_IMPORT_KEY);
          optionsRef.current.onComplete?.(data);
          cleanup();
        } else if (data.status === 'failed') {
          if (process.env.NODE_ENV === 'development') {
            console.log('KG Import Context: Job failed, stopping polling');
          }
          setIsPolling(false);
          const errorMsg = data.error_details || data.message || KG_PROGRESS_MESSAGES.failed;
          setError(errorMsg);

          // Error notification is handled by onError callback in component
          // to avoid duplicate notifications

          localStorage.removeItem(KG_ACTIVE_IMPORT_KEY);
          optionsRef.current.onError?.(errorMsg);
          cleanup();
        } else if (data.status === 'cancelled') {
          if (process.env.NODE_ENV === 'development') {
            console.log('KG Import Context: Job cancelled, stopping polling');
          }
          setIsPolling(false);

          if (optionsRef.current.notificationEnabled && !optionsRef.current.stickyNotifications) {
            addNotification({
              type: 'info',
              title: KG_PROGRESS_MESSAGES.cancelled,
              message: KG_PROGRESS_MESSAGES.cancelCompleted,
              duration: 3000
            });
          }

          localStorage.removeItem(KG_ACTIVE_IMPORT_KEY);
          optionsRef.current.onCancel?.();
          cleanup();
        } else {
          // For non-terminal states (pending, running), ensure polling continues
          if (process.env.NODE_ENV === 'development') {
            console.log('KG Import Context: Job still in progress, continuing polling. Status:', data.status);
          }
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : KG_ERROR_MESSAGES.networkError;

        if (process.env.NODE_ENV === 'development') {
          console.error('KG Import Context: Polling error:', errorMessage, error);
        }

        // Increment consecutive errors for circuit breaker
        consecutiveErrorsRef.current += 1;

        // Use current state for retry logic with exponential backoff
        setRetryCount((currentRetryCount) => {
          const { maxRetries: currentMaxRetries = 3, notificationEnabled: currentNotificationEnabled = true } = optionsRef.current;

          if (currentRetryCount < currentMaxRetries && consecutiveErrorsRef.current < 15) {
            // Exponential backoff: 2s, 4s, 8s, 16s, max 30s
            const backoffDelay = Math.min(2000 * Math.pow(2, currentRetryCount), 30000);

            if (process.env.NODE_ENV === 'development') {
              console.log(
                `KG Import Context: Retrying in ${backoffDelay}ms (attempt ${currentRetryCount + 1}/${currentMaxRetries}, consecutive errors: ${consecutiveErrorsRef.current})`
              );
            }

            if (currentNotificationEnabled) {
              addNotification({
                type: 'warning',
                title: KG_PROGRESS_MESSAGES.retrying,
                message: `Lần thử ${currentRetryCount + 1}/${currentMaxRetries}... (${backoffDelay / 1000}s)`,
                duration: 2000
              });
            }

            // Use timeout instead of recursive call - NO MORE RECURSIVE CHAINS
            retryTimeoutRef.current = setTimeout(() => {
              retryTimeoutRef.current = null;
              // Only retry if polling is still active and not cleaned up
              if (isPollingActiveRef.current) {
                isPollingActiveRef.current = false; // Reset flag for next poll
                pollProgress(jobId);
              }
            }, backoffDelay);

            return currentRetryCount + 1;
          } else {
            // Max retries reached or circuit breaker triggered
            if (process.env.NODE_ENV === 'development') {
              console.error('KG Import Context: Max retries reached or circuit breaker triggered, stopping polling');
            }
            setError(errorMessage);
            setIsPolling(false);

            if (currentNotificationEnabled) {
              addNotification({
                type: 'error',
                title: KG_ERROR_MESSAGES.networkError,
                message: `${errorMessage} (${currentMaxRetries} lần thử)`,
                duration: 8000
              });
            }

            optionsRef.current.onError?.(errorMessage);
            cleanup();
            return currentRetryCount;
          }
        });
      } finally {
        // Always reset the polling flag
        isPollingActiveRef.current = false;
      }
    },
    [getAuthToken, addNotification, removeNotification, cleanup, kgApi]
  );

  // Start progress tracking
  const startTracking = useCallback(
    (jobId: string, options: KGImportProgressOptions = {}) => {
      if (!jobId) {
        console.warn('KG Import Context: Cannot start tracking - no jobId provided');
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('KG Import Context: Starting progress tracking for job:', jobId);
      }

      // Update options ref
      optionsRef.current = {
        pollInterval: 3000, // More responsive polling
        maxRetries: 3,
        notificationEnabled: true,
        stickyNotifications: false,
        ...options
      };

      setIsPolling((currentIsPolling) => {
        if (currentIsPolling && activeJobId === jobId) {
          if (process.env.NODE_ENV === 'development') {
            console.log('KG Import Context: Already polling same job, ignoring start request');
          }
          return currentIsPolling;
        }

        // Clear any existing interval first
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }

        setActiveJobId(jobId);
        localStorage.setItem(KG_ACTIVE_IMPORT_KEY, jobId);
        setError(null);
        setRetryCount(0);
        setProgressData(null);
        setIsDismissed(false); // Reset dismissed state for new imports

        // Show initial notification for sticky mode
        const {
          notificationEnabled: currentNotificationEnabled,
          stickyNotifications: currentStickyNotifications,
          pollInterval: currentPollInterval
        } = optionsRef.current;

        if (currentNotificationEnabled && currentStickyNotifications) {
          const id = addNotification({
            type: 'info',
            title: KG_PROGRESS_MESSAGES.importStarted,
            message: KG_PROGRESS_MESSAGES.initializing,
            duration: 0,
            autoClose: false
          });
          setNotificationId(id);
          notificationIdRef.current = id;
        }

        // Wait 1 second before first poll to allow backend to create the job
        setTimeout(() => {
          pollProgress(jobId).catch((error) => {
            if (process.env.NODE_ENV === 'development') {
              console.error('KG Import Context: Initial poll failed:', error);
            }
          });
        }, 1000);

        // Set up polling interval with more robust error handling
        pollIntervalRef.current = setInterval(() => {
          if (process.env.NODE_ENV === 'development') {
            console.log('KG Import Context: Interval poll for job:', jobId);
          }
          pollProgress(jobId).catch((error) => {
            if (process.env.NODE_ENV === 'development') {
              console.error('KG Import Context: Interval poll failed:', error);
            }
          });
        }, currentPollInterval || 3000);

        if (process.env.NODE_ENV === 'development') {
          console.log(`KG Import Context: Polling interval set to ${currentPollInterval || 3000}ms`);
        }

        return true;
      });
    },
    [pollProgress, addNotification, activeJobId]
  );

  // Stop progress tracking
  const stopTracking = useCallback(() => {
    localStorage.removeItem(KG_ACTIVE_IMPORT_KEY);
    cleanup();
    setProgressData(null);
    setError(null);
    setActiveJobId(null);
  }, [cleanup]);

  // Cancel import job
  const cancelImport = useCallback(async () => {
    if (!activeJobId) return false;

    try {
      await kgApi.cancelImportJob(activeJobId);

      localStorage.removeItem(KG_ACTIVE_IMPORT_KEY);
      cleanup();
      setActiveJobId(null);

      if (optionsRef.current.notificationEnabled) {
        addNotification({
          type: 'info',
          title: KG_PROGRESS_MESSAGES.importCancelSuccess,
          message: KG_PROGRESS_MESSAGES.cancelCompleted,
          duration: 3000
        });
      }

      return true;
    } catch (error) {
      if (optionsRef.current.notificationEnabled) {
        addNotification({
          type: 'error',
          title: KG_PROGRESS_MESSAGES.importCancelFailed,
          message: error instanceof Error ? error.message : KG_PROGRESS_MESSAGES.cancelFailed,
          duration: 5000
        });
      }

      return false;
    }
  }, [kgApi, cleanup, addNotification, activeJobId]);

  // Dismiss the notification (but keep tracking active jobs)
  const dismissNotification = useCallback(() => {
    // Defer state update to avoid updating during render
    setTimeout(() => {
      setIsDismissed(true);
    }, 0);

    // Auto-clear dismiss state after 1 hour for failed/completed jobs
    setProgressData((currentProgressData) => {
      const currentStatus = currentProgressData?.status;
      if (currentStatus === 'completed' || currentStatus === 'failed' || currentStatus === 'cancelled') {
        setTimeout(
          () => {
            setProgressData((latestProgressData) => {
              if (latestProgressData?.status === currentStatus) {
                setIsDismissed(false);
                setActiveJobId(null);
                setError(null);
                return null;
              }
              return latestProgressData;
            });
          },
          60 * 60 * 1000
        ); // 1 hour
      }
      return currentProgressData;
    });
  }, []);

  // Suppress global indicator (when modal is showing progress inline)
  const setSuppressGlobalIndicator = useCallback((suppress: boolean) => {
    setIsSuppressed(suppress);
  }, []);

  // Retry failed import (resets dismiss state)
  const retryImport = useCallback(() => {
    if (!progressData || !activeJobId) return;

    setIsDismissed(false);
    setError(null);
    setRetryCount(0);

    // Restart tracking for the same job (if it supports retry)
    // Note: This assumes the backend supports restarting failed imports
    startTracking(activeJobId, optionsRef.current);
  }, [progressData, activeJobId, startTracking]);

  // Global cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Recover in-progress KG import after page reload
  useEffect(() => {
    const storedJobId = localStorage.getItem(KG_ACTIVE_IMPORT_KEY);
    if (!storedJobId) return;
    let cancelled = false;

    kgApi
      .getImportJobStatus(storedJobId)
      .then((data) => {
        if (cancelled) return;
        if (['completed', 'failed', 'cancelled', 'skipped'].includes(data.status)) {
          localStorage.removeItem(KG_ACTIVE_IMPORT_KEY);
        } else {
          startTracking(storedJobId, { notificationEnabled: true });
        }
      })
      .catch(() => {
        if (!cancelled) localStorage.removeItem(KG_ACTIVE_IMPORT_KEY);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue: KGImportProgressContextValue = useMemo(
    () => ({
      progressData,
      isPolling,
      error,
      retryCount,
      activeJobId,
      isDismissed,
      startTracking,
      stopTracking,
      cancelImport,
      dismissNotification,
      retryImport,
      setSuppressGlobalIndicator,
      isSuppressed,
      // Computed values for convenience
      isCompleted: progressData?.status === 'completed',
      isFailed: progressData?.status === 'failed',
      isCancelled: progressData?.status === 'cancelled',
      isSkipped: progressData?.status === 'skipped',
      isRunning: progressData?.status === 'running',
      progressPercentage: 0, // Deprecated - use allSteps for per-step progress
      currentStep: progressData?.current_step?.id ?? '',
      allSteps: progressData?.all_steps ?? [],
      documentsInfo: progressData
        ? {
          processed: progressData.processed_documents,
          total: progressData.total_documents,
          failed: progressData.failed_documents
        }
        : null,
      performanceInfo: progressData
        ? {
          nodesCreated: progressData.nodes_created ?? 0,
          relationshipsCreated: progressData.relationships_created ?? 0,
          embeddingsCreated: progressData.embeddings_created ?? 0,
          elapsedTime: progressData.elapsed_time
        }
        : null
    }),
    [
      progressData,
      isPolling,
      error,
      retryCount,
      activeJobId,
      isDismissed,
      startTracking,
      stopTracking,
      cancelImport,
      dismissNotification,
      retryImport,
      setSuppressGlobalIndicator,
      isSuppressed
    ]
  );

  return <KGImportProgressContext.Provider value={contextValue}>{children}</KGImportProgressContext.Provider>;
};

/**
 * Hook to use the global KG Import Progress Context
 */
export const useKGImportProgressContext = () => {
  const context = useContext(KGImportProgressContext);
  if (context === undefined) {
    throw new Error('useKGImportProgressContext must be used within a KGImportProgressProvider');
  }
  return context;
};
